/**
 * Playlists Management — admins configure provider URLs that devices fetch
 * directly. Supports both raw M3U and Xtream Codes URL pairs.
 */
import React, { useEffect, useState } from "react";
import {
  listPlaylists,
  createPlaylist,
  updatePlaylist,
  deletePlaylist,
  getCoverStats,
  refreshCovers,
  setPlaylistDebugMode,
  setPlaylistB2cEnabled,
  getPlaylistHarvestStatus,
  runPlaylistHarvest,
  deletePlaylistSnapshot,
  setPlaylistSnapshotEnabled,
  getPlaylistSnapshotStatus,
  runPlaylistSnapshot,
  deletePlaylistPreparsedSnapshot,
  getPlaylistPrewarmStatus,
  runPlaylistPrewarm,
  deletePlaylistPrewarm,
  unlockPlaylist,
} from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, ListVideo, Image as ImageIcon, RefreshCw, Bug, Zap, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ADMIN } from "../lib/testIds";
import CoverInspectorDialog from "./CoverInspectorDialog";
import { useAuth } from "../lib/AuthContext";

export default function Playlists() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);

  // v1.4.38 — Playlist Lock (reseller-only). When the caller has a
  // playlist password set, the backend returns every row with `is_locked
  // = true` and mask (••••••) all URL fields. Editing / viewing a
  // locked row requires entering the password every time (2a-scope).
  //
  // `pendingUnlock`: playlist being unlocked before opening the Edit
  //   dialog. `null` = idle, otherwise `{ playlist, error, submitting }`.
  // `unlockPw`: password bound to the prompt Input.
  // `editPassword`: the plaintext password captured from a successful
  //   unlock. Held only for the duration of the Edit dialog so
  //   handleEditSave can forward it via the X-Playlist-Password header.
  const [pendingUnlock, setPendingUnlock] = useState(null);
  const [unlockPw, setUnlockPw] = useState("");
  const [unlockErr, setUnlockErr] = useState("");
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [editPassword, setEditPassword] = useState("");
  // Cover-cache state — pulled from /api/admin/covers/stats. Refreshed
  // on mount and every time we fire a harvest. Keyed by playlist_id so
  // each row can show "X cached / Y MB on disk" inline.
  const [coverStats, setCoverStats] = useState(null);
  const [coverBusy, setCoverBusy] = useState({}); // { [playlist_id]: true }
  // Inspector dialog state — set to a playlist row to open the modal.
  const [inspecting, setInspecting] = useState(null);

  async function reloadCoverStats() {
    try {
      setCoverStats(await getCoverStats());
    } catch {
      /* non-fatal — the card just stays "—" */
    }
  }

  async function handleRefreshCovers(playlistId, refreshAll = false) {
    setCoverBusy((b) => ({ ...b, [playlistId]: true }));
    try {
      await refreshCovers(playlistId, refreshAll);
      toast({
        title: "Cover harvest started",
        description:
          "Walking the Xtream catalog and downloading every Plex cover. " +
          "Stats below refresh every 5 s while it runs.",
      });
    } catch (e) {
      toast({
        title: "Couldn't start harvest",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setCoverBusy((b) => ({ ...b, [playlistId]: false }));
    }
  }

  // v1.3.41 — optimistic toggle of the per-playlist Debug-mode flag.
  // Server is the source of truth; on failure we revert the row.
  async function handleTogglePlaylistDebug(p, next) {
    setPlaylists((prev) => prev.map((x) => x.id === p.id ? { ...x, debug_mode_enabled: !!next } : x));
    try {
      await setPlaylistDebugMode(p.id, !!next);
      toast({
        title: next ? "Debug mode enabled" : "Debug mode disabled",
        description: `Devices using "${p.name}" will pick up the change on next refresh.`,
      });
    } catch (e) {
      setPlaylists((prev) => prev.map((x) => x.id === p.id ? { ...x, debug_mode_enabled: !next } : x));
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // v1.3.44 — Cover & Metadata Harvest controls (per playlist).
  // `harvestStatus[playlistId]` holds the last-known snapshot meta.
  const [harvestStatus, setHarvestStatus] = useState({});
  const [harvestBusy, setHarvestBusy] = useState({});

  async function refreshHarvestStatus(playlistId) {
    try {
      const data = await getPlaylistHarvestStatus(playlistId);
      setHarvestStatus((s) => ({ ...s, [playlistId]: data }));
    } catch { /* ignore — UI just shows "—" */ }
  }

  async function handleToggleB2cEnabled(p, next) {
    setPlaylists((prev) => prev.map((x) => x.id === p.id ? { ...x, b2c_enabled: !!next } : x));
    try {
      await setPlaylistB2cEnabled(p.id, !!next);
      toast({
        title: next ? "Harvest enabled" : "Harvest disabled",
        description: next
          ? `Devices using "${p.name}" will fetch the server snapshot for covers + metadata.`
          : `Devices using "${p.name}" will skip the metadata-overlay step entirely.`,
      });
      refreshHarvestStatus(p.id);
    } catch (e) {
      setPlaylists((prev) => prev.map((x) => x.id === p.id ? { ...x, b2c_enabled: !next } : x));
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  async function handleRunHarvest(p) {
    setHarvestBusy((b) => ({ ...b, [p.id]: true }));
    // v1.3.49 — ALWAYS force the toggle ON when Run now is pressed
    // (previously only when explicitly false). Some legacy playlists
    // have `b2c_enabled === undefined` which my earlier conditional
    // skipped, leaving admins with a "snapshot exists but ignored"
    // state that's confusing to diagnose.
    try {
      await setPlaylistB2cEnabled(p.id, true);
      setPlaylists((prev) =>
        prev.map((x) => (x.id === p.id ? { ...x, b2c_enabled: true } : x)),
      );
    } catch { /* don't block the harvest on this */ }
    try {
      await runPlaylistHarvest(p.id);
      toast({
        title: "Harvest started",
        description: `Server is fetching the catalog for "${p.name}". This usually takes 30-90 s.`,
      });
      // Optimistic refresh — poll every 5 s for up to 3 minutes so the
      // status pill updates the moment the harvest finishes without
      // requiring an admin page reload.
      let polls = 0;
      const tick = async () => {
        polls += 1;
        await refreshHarvestStatus(p.id);
        const st = harvestStatus[p.id];
        if (polls < 36 && (st?.running || !st?.snapshot)) {
          setTimeout(tick, 5000);
        } else {
          setHarvestBusy((b) => ({ ...b, [p.id]: false }));
        }
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setHarvestBusy((b) => ({ ...b, [p.id]: false }));
      toast({
        title: "Couldn't start harvest",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  async function handleDeleteSnapshot(p) {
    try {
      await deletePlaylistSnapshot(p.id);
      toast({
        title: "Snapshot deleted",
        description: `Devices will fall back to client-side enrichment until you run a fresh harvest.`,
      });
      refreshHarvestStatus(p.id);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // v1.3.48 — Playlist Pre-parse Snapshot (the M3U fetch + parse that
  // used to run on every device). Same UX shape as the metadata harvest.
  const [snapStatus, setSnapStatus] = useState({});
  const [snapBusy, setSnapBusy] = useState({});

  async function refreshSnapStatus(playlistId) {
    try {
      const data = await getPlaylistSnapshotStatus(playlistId);
      setSnapStatus((s) => ({ ...s, [playlistId]: data }));
    } catch { /* ignore */ }
  }

  async function handleToggleSnapshotEnabled(p, next) {
    setPlaylists((prev) =>
      prev.map((x) =>
        x.id === p.id ? { ...x, playlist_snapshot_enabled: !!next } : x,
      ),
    );
    try {
      await setPlaylistSnapshotEnabled(p.id, !!next);
      toast({
        title: next ? "Playlist snapshot enabled" : "Playlist snapshot disabled",
        description: next
          ? `Devices using "${p.name}" will download the pre-parsed snapshot instead of fetching and parsing M3U on cold start.`
          : `Devices using "${p.name}" will go back to the slow M3U streaming parse.`,
      });
      refreshSnapStatus(p.id);
    } catch (e) {
      setPlaylists((prev) =>
        prev.map((x) =>
          x.id === p.id ? { ...x, playlist_snapshot_enabled: !next } : x,
        ),
      );
      toast({
        title: "Toggle failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  async function handleRunSnapshot(p) {
    setSnapBusy((b) => ({ ...b, [p.id]: true }));
    // v1.3.49 — ALWAYS force the toggle ON when Run now is pressed
    // (see comment on handleRunHarvest for rationale).
    try {
      await setPlaylistSnapshotEnabled(p.id, true);
      setPlaylists((prev) =>
        prev.map((x) =>
          x.id === p.id ? { ...x, playlist_snapshot_enabled: true } : x,
        ),
      );
    } catch { /* admin can toggle manually if this fails */ }
    try {
      await runPlaylistSnapshot(p.id);
      toast({
        title: "Pre-parse started",
        description: `Server is downloading and parsing "${p.name}". 1-3 min for huge catalogs.`,
      });
      let polls = 0;
      const tick = async () => {
        polls += 1;
        await refreshSnapStatus(p.id);
        const st = snapStatus[p.id];
        if (polls < 60 && (st?.running || !st?.snapshot)) {
          setTimeout(tick, 5000);
        } else {
          setSnapBusy((b) => ({ ...b, [p.id]: false }));
        }
      };
      setTimeout(tick, 3000);
    } catch (e) {
      setSnapBusy((b) => ({ ...b, [p.id]: false }));
      toast({
        title: "Couldn't start pre-parse",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  async function handleDeletePreparsedSnapshot(p) {
    try {
      await deletePlaylistPreparsedSnapshot(p.id);
      toast({
        title: "Pre-parsed snapshot deleted",
        description: `Devices will fall back to the streaming M3U parse on next refresh.`,
      });
      refreshSnapStatus(p.id);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // v1.3.70 — Xtream pre-warm catalog (the gzipped JSON bundle the
  // player downloads on cold start to skip N per-category fetches).
  // Mirrors the harvest/snapshot UX shape.
  const [prewarmStatus, setPrewarmStatus] = useState({});
  const [prewarmBusy, setPrewarmBusy] = useState({});

  async function refreshPrewarmStatus(playlistId) {
    try {
      const resp = await getPlaylistPrewarmStatus(playlistId);
      setPrewarmStatus((s) => ({ ...s, [playlistId]: resp?.data || resp }));
    } catch { /* ignore — UI just shows "—" */ }
  }

  async function handleRunPrewarm(p) {
    setPrewarmBusy((b) => ({ ...b, [p.id]: true }));
    try {
      await runPlaylistPrewarm(p.id);
      toast({
        title: "Pre-warm harvest started",
        description: `Server is fetching the Xtream catalog for "${p.name}". 30-120 s depending on size.`,
      });
      // Poll every 4 s until done/error or 5 minutes elapsed.
      let polls = 0;
      const tick = async () => {
        polls += 1;
        await refreshPrewarmStatus(p.id);
        const st = prewarmStatus[p.id]?.status;
        const running = st?.status === "running";
        if (polls < 75 && running) {
          setTimeout(tick, 4000);
        } else {
          setPrewarmBusy((b) => ({ ...b, [p.id]: false }));
        }
      };
      setTimeout(tick, 2000);
    } catch (e) {
      setPrewarmBusy((b) => ({ ...b, [p.id]: false }));
      toast({
        title: "Couldn't start pre-warm",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  async function handleDeletePrewarm(p) {
    if (!window.confirm(`Delete the pre-warm bundle for "${p.name}"? Devices will fall back to per-category fetches until you re-harvest.`)) return;
    try {
      await deletePlaylistPrewarm(p.id);
      toast({
        title: "Pre-warm bundle deleted",
        description: `Devices will hit the upstream Xtream API directly for "${p.name}" until you re-harvest.`,
      });
      refreshPrewarmStatus(p.id);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  }

  // Poll cover stats while any harvest is running OR every 30 s
  // otherwise (so the page stays roughly accurate without hammering
  // the DB). Cheap aggregate — three small numbers.
  useEffect(() => {
    reloadCoverStats();
    const id = setInterval(reloadCoverStats, 5000);
    return () => clearInterval(id);
  }, []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    // `source_type` toggles the Add Playlist dialog between two input
    // modes (ITVZA-style). "xtream" = host + port + username + password
    // (Xtream Codes API), "raw" = paste a full M3U URL (+ optional EPG
    // URL). When the user pastes a get.php URL we still try to parse
    // out the credentials so the b2c_url can be regenerated from them.
    source_type: "xtream",
    host: "",
    port: "",
    username: "",
    password: "",
    // Raw mode fields. The admin must supply a name themselves because
    // we can't derive one from a freeform URL.
    name: "",
    m3u_url: "",
    epg_url: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Edit-playlist dialog state — opens when admin taps the pencil icon
  // on a row. We edit the resolved URLs directly (not the Xtream
  // host/user/pass triplet) because already-created playlists can be a
  // mix of raw M3U URLs and Xtream lines.
  const [editing, setEditing] = useState(null); // playlist or null
  const [editForm, setEditForm] = useState({
    name: "",
    source_type: "xtream",
    host: "",
    port: "",
    username: "",
    password: "",
    m3u_url: "", // only used when source_type === "raw"
    epg_url: "", // only used when source_type === "raw"
    description: "",
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  const openEdit = (p) => {
    // v1.4.38 — Locked rows must be unlocked (password verified)
    // before we can populate the Edit form with real URLs. The
    // password prompt calls /unlock; on success we get the unmasked
    // playlist back and open Edit with it.
    if (p.is_locked) {
      setPendingUnlock({ playlist: p, mode: "edit" });
      setUnlockPw("");
      setUnlockErr("");
      return;
    }
    setEditPassword("");
    setEditing(p);
    const parsed = parsePlaylistUrls(p.m3u_url, p.epg_url);
    setEditForm({
      name: p.name || "",
      source_type: parsed.source_type,
      host: parsed.host || "",
      port: parsed.port || "",
      username: parsed.username || "",
      password: parsed.password || "",
      m3u_url: parsed.m3u_url || p.m3u_url || "",
      epg_url: parsed.epg_url || p.epg_url || "",
      description: p.description || "",
    });
  };

  // v1.4.38 — Verify the reseller's playlist password. On success,
  // opens the Edit dialog pre-populated with the unmasked playlist
  // and remembers the password for the subsequent PUT.
  const handleUnlockSubmit = async () => {
    if (!pendingUnlock || !unlockPw) {
      setUnlockErr("Enter your playlist password");
      return;
    }
    setUnlockBusy(true);
    setUnlockErr("");
    try {
      const { playlist: unmasked } = await unlockPlaylist(pendingUnlock.playlist.id, unlockPw);
      const parsed = parsePlaylistUrls(unmasked.m3u_url, unmasked.epg_url);
      setEditPassword(unlockPw);
      setEditing(unmasked);
      setEditForm({
        name: unmasked.name || "",
        source_type: parsed.source_type,
        host: parsed.host || "",
        port: parsed.port || "",
        username: parsed.username || "",
        password: parsed.password || "",
        m3u_url: parsed.m3u_url || unmasked.m3u_url || "",
        epg_url: parsed.epg_url || unmasked.epg_url || "",
        description: unmasked.description || "",
      });
      setPendingUnlock(null);
      setUnlockPw("");
    } catch (e) {
      setUnlockErr(e.response?.status === 403
        ? "Wrong playlist password"
        : (e.response?.data?.detail || e.message));
    } finally {
      setUnlockBusy(false);
    }
  };

  const handleEditSave = async () => {
    if (!editing) return;
    if (!editForm.name.trim()) {
      toast({
        title: "Missing fields",
        description: "Name is required.",
        variant: "destructive",
      });
      return;
    }
    // Build URLs from the structured fields when the user picked
    // xtream/b2c; for raw playlists the user edits the URL directly so
    // we forward their values verbatim.
    let m3u_url;
    let epg_url;
    let b2c_url;
    if (editForm.source_type === "raw") {
      m3u_url = editForm.m3u_url.trim();
      epg_url = editForm.epg_url.trim() || null;
      b2c_url = null;
    } else {
      const built = buildUrlsForForm(editForm);
      m3u_url = built.m3u_url;
      epg_url = built.epg_url || null;
      b2c_url = built.b2c_url || null;
    }
    if (!m3u_url) {
      toast({ title: "Invalid host", variant: "destructive" });
      return;
    }
    setEditSubmitting(true);
    try {
      await updatePlaylist(editing.id, {
        name: editForm.name.trim(),
        m3u_url,
        epg_url,
        b2c_url,
        description: editForm.description.trim() || null,
      }, editPassword || undefined);
      toast({
        title: "Playlist updated",
        description: "Devices will pick up the change on next refresh.",
      });
      setEditing(null);
      setEditPassword("");
      refresh();
    } catch (e) {
      toast({
        title: "Error updating playlist",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setEditSubmitting(false);
    }
  };

  // Build standard Xtream Codes playlist URLs from host/port/user/pass.
  //   m3u: http(s)://host:port/get.php?username=...&password=...&type=m3u_plus&output=m3u8
  //   epg: http(s)://host:port/xmltv.php?username=...&password=...
  // Saved as the playlist's primary streaming URLs so native players
  // (ExoPlayer, HLS.js, VLC) play directly without any B2C-protocol
  // shim. The separate `b2c_url` field is what the player uses to
  // fetch poster artwork / VOD metadata.
  const buildXtreamUrls = ({ host, port, username, password }) => {
    let h = (host || "").trim().replace(/\/+$/, "");
    if (!h) return { m3u_url: "", epg_url: "" };
    if (!/^https?:\/\//i.test(h)) h = `http://${h}`;
    const hasPortInHost = /:\d+$/.test(h.replace(/^https?:\/\//i, ""));
    const portStr = String(port || "").trim();
    if (portStr && !hasPortInHost) h = `${h}:${portStr}`;
    const u = encodeURIComponent(username || "");
    const p = encodeURIComponent(password || "");
    return {
      m3u_url: `${h}/get.php?username=${u}&password=${p}&type=m3u_plus&output=m3u8`,
      epg_url: `${h}/xmltv.php?username=${u}&password=${p}`,
    };
  };

  // Build a 1-Stream B2C "Quick Connect" URL. The player auto-detects the
  // `b2c://` scheme and switches to the paginated B2C REST pipeline (live
  // + VOD + series + EPG + statistics) instead of M3U parsing. We encode
  // an http→https hint as a `https-` prefix on the host because URL
  // schemes don't nest.
  const buildB2cUrl = ({ host, port, username, password }) => {
    let h = (host || "").trim().replace(/\/+$/, "");
    if (!h) return { m3u_url: "" };
    let useHttps = false;
    if (/^https:\/\//i.test(h)) { useHttps = true; h = h.replace(/^https:\/\//i, ""); }
    else if (/^http:\/\//i.test(h)) { h = h.replace(/^http:\/\//i, ""); }
    const hasPortInHost = /:\d+$/.test(h);
    const portStr = String(port || "").trim();
    if (portStr && !hasPortInHost) h = `${h}:${portStr}`;
    const u = encodeURIComponent(username || "");
    const p = encodeURIComponent(password || "");
    return { m3u_url: `b2c://${u}:${p}@${useHttps ? "https-" : ""}${h}` };
  };

  // Build ALL three URLs from one credential set: an Xtream M3U + EPG
  // pair for streaming, plus a b2c:// URL for metadata enrichment.
  const buildUrlsForForm = (f) => {
    const x = buildXtreamUrls(f);
    const b = buildB2cUrl(f);
    return {
      m3u_url: x.m3u_url,
      epg_url: x.epg_url,
      b2c_url: b.m3u_url || null,
    };
  };

  // Reverse an existing playlist's stored URLs back into the structured
  // host / port / username / password fields the Add dialog uses. We
  // recognise three shapes:
  //   - b2c://user:pass@host[:port]     → 1-Stream Quick Connect
  //   - http(s)://host:port/get.php?... → Xtream Codes
  //   - anything else                   → raw (read-only URL fields)
  // When raw is returned, the Edit dialog falls back to the legacy
  // M3U URL / EPG URL inputs.
  const parsePlaylistUrls = (m3u_url, epg_url) => {
    const url = String(m3u_url || "").trim();

    if (url.toLowerCase().startsWith("b2c://")) {
      try {
        const after = url.slice(6);
        const slashIdx = after.indexOf("/");
        const authority = slashIdx === -1 ? after : after.slice(0, slashIdx);
        const atIdx = authority.lastIndexOf("@");
        if (atIdx === -1) return { source_type: "raw" };
        const userinfo = authority.slice(0, atIdx);
        let hostport = authority.slice(atIdx + 1);
        const colonIdx = userinfo.indexOf(":");
        if (colonIdx === -1) return { source_type: "raw" };
        const username = decodeURIComponent(userinfo.slice(0, colonIdx));
        const password = decodeURIComponent(userinfo.slice(colonIdx + 1));
        let scheme = "";
        if (hostport.toLowerCase().startsWith("https-")) {
          scheme = "https://";
          hostport = hostport.slice(6);
        }
        const hpMatch = hostport.match(/^([^:]+)(?::(\d+))?$/);
        const host = scheme + (hpMatch?.[1] || hostport);
        const port = hpMatch?.[2] || "";
        return { source_type: "b2c", host, port, username, password };
      } catch {
        return { source_type: "raw" };
      }
    }

    // Xtream get.php pattern. The query string carries username + password
    // in plain text — we pull them straight from URLSearchParams and the
    // host/port from the URL constructor.
    try {
      const u = new URL(url);
      const params = u.searchParams;
      if (
        (u.pathname.endsWith("/get.php") || u.pathname.includes("/get.php")) &&
        params.get("username") &&
        params.get("password")
      ) {
        return {
          source_type: "xtream",
          host: `${u.protocol}//${u.hostname}`,
          port: u.port || "",
          username: params.get("username"),
          password: params.get("password"),
        };
      }
    } catch {
      /* fall through */
    }

    return { source_type: "raw", m3u_url: url, epg_url: epg_url || "" };
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await listPlaylists();
      setPlaylists(list);
      // v1.3.44 — hydrate harvest status for every playlist that has
      // b2c credentials so the Harvest column doesn't render empty.
      list.forEach((p) => {
        if (p.b2c_url) refreshHarvestStatus(p.id);
        if (p.m3u_url && p.m3u_url.startsWith("http")) refreshSnapStatus(p.id);
        // Pre-warm only makes sense for Xtream get.php URLs. Cheap probe.
        if (p.m3u_url && p.m3u_url.includes("/get.php")) refreshPrewarmStatus(p.id);
      });
    } catch (e) {
      toast({ title: "Failed to load", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async () => {
    // ---------- RAW M3U URL MODE ----------
    // Admin pasted a full M3U URL. If it happens to be a /get.php URL
    // we reverse-engineer the credentials so we can still generate a
    // b2c_url (used by the poster/metadata pipeline). Otherwise we
    // save it as-is with no b2c_url.
    if (form.source_type === "raw") {
      const rawM3u = form.m3u_url.trim();
      const rawName = form.name.trim();
      if (!rawM3u) {
        toast({
          title: "Missing fields",
          description: "M3U URL is required.",
          variant: "destructive",
        });
        return;
      }
      if (!rawName) {
        toast({
          title: "Missing fields",
          description: "Name is required for M3U URL playlists.",
          variant: "destructive",
        });
        return;
      }
      let b2c_url = null;
      const parsed = parsePlaylistUrls(rawM3u, form.epg_url.trim());
      if (parsed.source_type === "xtream" || parsed.source_type === "b2c") {
        const built = buildB2cUrl(parsed);
        b2c_url = built.m3u_url || null;
      }
      setSubmitting(true);
      try {
        await createPlaylist({
          name: rawName,
          m3u_url: rawM3u,
          epg_url: form.epg_url.trim() || null,
          b2c_url,
          description: form.description || null,
        });
        toast({
          title: "Playlist saved",
          description: "Devices will use this playlist next time they refresh.",
        });
        setOpen(false);
        setForm({
          source_type: "xtream",
          host: "",
          port: "",
          username: "",
          password: "",
          name: "",
          m3u_url: "",
          epg_url: "",
          description: "",
        });
        refresh();
      } catch (e) {
        toast({
          title: "Error creating playlist",
          description: e.response?.data?.detail || e.message,
          variant: "destructive",
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // ---------- XTREAM CODES API MODE ----------
    if (!form.host || !form.username || !form.password) {
      toast({
        title: "Missing fields",
        description: "Host, username and password are required.",
        variant: "destructive",
      });
      return;
    }
    const { m3u_url, epg_url, b2c_url } = buildUrlsForForm(form);
    if (!m3u_url) {
      toast({ title: "Invalid host", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await createPlaylist({
        // Playlist name = username, per admin policy. Easier to identify
        // which Xtream line a row corresponds to at a glance.
        name: form.username,
        m3u_url,
        epg_url: epg_url || null,
        b2c_url: b2c_url || null,
        description: form.description || null,
      });
      toast({
        title: "Playlist saved",
        description: "Devices will use this playlist next time they refresh.",
      });
      setOpen(false);
      setForm({
        source_type: "xtream",
        host: "",
        port: "",
        username: "",
        password: "",
        name: "",
        m3u_url: "",
        epg_url: "",
        description: "",
      });
      refresh();
    } catch (e) {
      toast({
        title: "Error creating playlist",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this playlist and remove it from all assigned keys?")) return;
    try {
      await deletePlaylist(id);
      toast({ title: "Playlist deleted" });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <p className="text-slate-600 max-w-2xl">
          Add IPTV provider playlists, then assign them to activation keys so
          devices can pick which one to load.
        </p>
        <Button
          data-testid={ADMIN.btnAddPlaylist}
          onClick={() => setOpen(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Playlist
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : playlists.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <ListVideo className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No playlists yet. Add your first M3U URL.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>M3U URL</TableHead>
                  <TableHead>EPG URL</TableHead>
                  <TableHead>B2C URL</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Last requested</TableHead>
                  <TableHead className="text-center">Debug</TableHead>
                  <TableHead className="min-w-[260px]">Pre-warm catalog</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {playlists.map((p) => {
                  // The `b2c_url` field is the source of truth — written
                  // by the admin save flow. For legacy rows that don't
                  // have one yet, we derive it on the fly from the
                  // structured credentials embedded in the M3U URL so
                  // the column never reads blank for an Xtream provider.
                  // v1.4.38 — If the row is locked (reseller w/
                  // playlist-lock), just render •••••• for B2C too;
                  // the raw m3u_url is masked so parsePlaylistUrls
                  // would garble anyway.
                  let b2cMasked = "—";
                  if (p.is_locked) {
                    b2cMasked = "\u2022\u2022\u2022\u2022\u2022\u2022";
                  } else if (p.b2c_url) {
                    b2cMasked = p.b2c_url.replace(
                      /(b2c:\/\/)[^:]+:[^@]+@/,
                      "$1***:***@"
                    );
                  } else {
                    const parsed = parsePlaylistUrls(p.m3u_url, p.epg_url);
                    if (parsed.source_type === "b2c" || parsed.source_type === "xtream") {
                      const built = buildB2cUrl(parsed);
                      if (built.m3u_url) {
                        b2cMasked = built.m3u_url.replace(
                          /(b2c:\/\/)[^:]+:[^@]+@/,
                          "$1***:***@"
                        );
                      }
                    }
                  }
                  return (
                  <TableRow key={p.id} data-testid={ADMIN.playlistRow(p.id)}>
                    <TableCell className="font-semibold">
                      <span className="inline-flex items-center gap-1.5">
                        {p.is_locked && (
                          <Lock
                            className="w-3.5 h-3.5 text-orange-500 shrink-0"
                            data-testid={`playlist-lock-icon-${p.id}`}
                            title="Locked — enter your playlist password to view or edit"
                          />
                        )}
                        {p.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      {/* v1.4.66 — Owner badge (admin / reseller / client) */}
                      {p.owner_username ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <span className="text-xs font-medium text-slate-800">
                            {p.owner_username}
                          </span>
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                              p.owner_role === "admin"
                                ? "bg-purple-100 text-purple-700"
                                : p.owner_role === "reseller"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-100 text-slate-600"
                            }`}
                            data-testid={`playlist-owner-role-${p.id}`}
                          >
                            {p.owner_role || "client"}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono max-w-xs truncate" title={p.m3u_url}>
                      {p.m3u_url}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono max-w-xs truncate" title={p.epg_url || ""}>
                      {p.epg_url || <span className="text-slate-400 italic">auto from M3U</span>}
                    </TableCell>
                    <TableCell
                      className="text-xs text-orange-700 font-mono max-w-xs truncate"
                      title={b2cMasked}
                      data-testid={`playlist-b2c-url-${p.id}`}
                    >
                      {b2cMasked === "—" ? (
                        <span className="text-slate-400 italic">—</span>
                      ) : (
                        b2cMasked
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">
                      {p.description || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500" data-testid={`admin-playlist-last-requested-${p.id}`}>
                      {p.last_requested_at
                        ? (() => {
                            try { return new Date(p.last_requested_at).toLocaleString(); }
                            catch { return p.last_requested_at; }
                          })()
                        : <span className="text-slate-400 italic">never</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        data-testid={`admin-playlist-debug-${p.id}`}
                        checked={!!p.debug_mode_enabled}
                        onCheckedChange={(v) => handleTogglePlaylistDebug(p, v)}
                        title="Force debug mode on every device using this playlist"
                      />
                    </TableCell>
                    <TableCell className="align-top" data-testid={`admin-playlist-prewarm-${p.id}`}>
                      <PrewarmCell
                        playlist={p}
                        info={prewarmStatus[p.id]}
                        busy={!!prewarmBusy[p.id]}
                        onRun={() => handleRunPrewarm(p)}
                        onDelete={() => handleDeletePrewarm(p)}
                        onRefresh={() => refreshPrewarmStatus(p.id)}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={`btn-edit-playlist-${p.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => openEdit(p)}
                        className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 mr-1"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        data-testid={ADMIN.btnDeletePlaylist(p.id)}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(p.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={ADMIN.addPlaylistDialog}>
          <DialogHeader>
            <DialogTitle>Add Playlist</DialogTitle>
            <DialogDescription>
              Choose how to add this provider — paste Xtream Codes API
              credentials (host + username + password) or a full M3U URL.
              Xtream mode auto-generates the M3U, EPG and B2C URLs.
            </DialogDescription>
          </DialogHeader>

          {/* Source-type toggle — ITVZA-style: XTREAM CODES API vs M3U URL.
              Stored on the form so handleCreate can branch correctly. */}
          <Tabs
            value={form.source_type}
            onValueChange={(v) => setForm({ ...form, source_type: v })}
            className="w-full"
          >
            <TabsList className="grid grid-cols-2 w-full" data-testid="playlist-source-tabs">
              <TabsTrigger value="xtream" data-testid="playlist-source-xtream">
                Xtream Codes API
              </TabsTrigger>
              <TabsTrigger value="raw" data-testid="playlist-source-m3u">
                M3U URL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="xtream" className="space-y-3 mt-3">
              <div className="grid grid-cols-[1fr_120px] gap-3">
                <div>
                  <Label htmlFor="host">Host URL *</Label>
                  <Input
                    id="host"
                    data-testid="playlist-host"
                    value={form.host}
                    onChange={(e) => setForm({ ...form, host: e.target.value })}
                    placeholder="e.g. http://provider.com"
                  />
                </div>
                <div>
                  <Label htmlFor="port">Port</Label>
                  <Input
                    id="port"
                    data-testid="playlist-port"
                    value={form.port}
                    onChange={(e) => setForm({ ...form, port: e.target.value })}
                    placeholder="80"
                    inputMode="numeric"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="username">
                    Username * <span className="text-[10px] font-normal text-slate-500">(used as playlist name)</span>
                  </Label>
                  <Input
                    id="username"
                    data-testid={ADMIN.playlistName}
                    value={form.username}
                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                    placeholder="username"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    data-testid="playlist-password"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="password"
                    autoComplete="off"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="raw" className="space-y-3 mt-3">
              <div>
                <Label htmlFor="pl-name-raw">Name *</Label>
                <Input
                  id="pl-name-raw"
                  data-testid="playlist-name-raw"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. MyProvider"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label htmlFor="pl-m3u-raw">M3U URL *</Label>
                <Input
                  id="pl-m3u-raw"
                  data-testid="playlist-m3u-raw"
                  value={form.m3u_url}
                  onChange={(e) => setForm({ ...form, m3u_url: e.target.value })}
                  placeholder="http://host:port/get.php?username=…&password=…&type=m3u_plus&output=m3u8"
                  className="font-mono text-xs"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  If this is an Xtream <code>/get.php</code> URL, the credentials
                  are auto-extracted to also generate a B2C metadata URL.
                </p>
              </div>
              <div>
                <Label htmlFor="pl-epg-raw">EPG URL (optional)</Label>
                <Input
                  id="pl-epg-raw"
                  data-testid="playlist-epg-raw"
                  value={form.epg_url}
                  onChange={(e) => setForm({ ...form, epg_url: e.target.value })}
                  placeholder="http://host:port/xmltv.php?username=…"
                  className="font-mono text-xs"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Leave blank to auto-detect the EPG URL from the M3U header.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <div className="space-y-3 mt-3">
            <div>
              <Label htmlFor="desc">Description (optional)</Label>
              <Textarea
                id="desc"
                data-testid={ADMIN.playlistDescription}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Notes about this playlist..."
                rows={2}
              />
            </div>

            {/* Live preview of all THREE URLs that will be saved:
                  - M3U (Xtream get.php) for native streaming
                  - EPG (Xtream xmltv.php) for guide
                  - B2C (b2c://…) for poster + metadata enrichment
                The B2C credentials are masked in the preview. */}
            {form.source_type === "xtream" && form.host && form.username && form.password && (
              <div className="rounded-md border border-orange-200 bg-orange-50/60 px-3 py-2 text-[11px] font-mono text-orange-900 space-y-1 break-all">
                <div>
                  <span className="font-sans font-semibold text-orange-700">M3U:</span>{" "}
                  {buildXtreamUrls(form).m3u_url}
                </div>
                <div>
                  <span className="font-sans font-semibold text-orange-700">EPG:</span>{" "}
                  {buildXtreamUrls(form).epg_url}
                </div>
                <div>
                  <span className="font-sans font-semibold text-orange-700">B2C:</span>{" "}
                  {buildB2cUrl(form).m3u_url.replace(
                    /(b2c:\/\/)[^:]+:[^@]+@/,
                    "$1***:***@"
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={ADMIN.playlistSave}
              onClick={handleCreate}
              disabled={submitting}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog — unified single form. Old playlists stored as
          raw M3U URLs still fall back to the legacy direct-URL inputs
          (backward compatibility); everything else is treated as the
          unified Xtream/B2C credential triple. */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-playlist-dialog">
          <DialogHeader>
            <DialogTitle>Edit Playlist</DialogTitle>
            <DialogDescription>
              {editForm.source_type === "raw"
                ? "Update the playlist name or URLs directly. Existing devices will pull the new URLs on next refresh."
                : "Update the provider credentials. Devices will pull the new credentials and re-sync catalogue + posters on next refresh."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="edit-pl-name">Name</Label>
              <Input
                id="edit-pl-name"
                data-testid="edit-pl-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="e.g. john_doe"
              />
            </div>
            {editForm.source_type === "raw" ? (
              <>
                <div>
                  <Label htmlFor="edit-pl-m3u">M3U URL</Label>
                  <Input
                    id="edit-pl-m3u"
                    data-testid="edit-pl-m3u"
                    value={editForm.m3u_url}
                    onChange={(e) =>
                      setEditForm({ ...editForm, m3u_url: e.target.value })
                    }
                    placeholder="http://host:port/get.php?username=…"
                    className="font-mono text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-pl-epg">EPG URL (optional)</Label>
                  <Input
                    id="edit-pl-epg"
                    data-testid="edit-pl-epg"
                    value={editForm.epg_url}
                    onChange={(e) =>
                      setEditForm({ ...editForm, epg_url: e.target.value })
                    }
                    placeholder="http://host:port/xmltv.php?username=…"
                    className="font-mono text-xs"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Leave blank to auto-detect the EPG URL from the M3U header.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_120px] gap-3">
                  <div>
                    <Label htmlFor="edit-host">Host URL *</Label>
                    <Input
                      id="edit-host"
                      data-testid="edit-playlist-host"
                      value={editForm.host}
                      onChange={(e) => setEditForm({ ...editForm, host: e.target.value })}
                      placeholder="e.g. http://provider.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-port">Port</Label>
                    <Input
                      id="edit-port"
                      data-testid="edit-playlist-port"
                      value={editForm.port}
                      onChange={(e) => setEditForm({ ...editForm, port: e.target.value })}
                      placeholder="80"
                      inputMode="numeric"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-username">Username *</Label>
                    <Input
                      id="edit-username"
                      data-testid="edit-playlist-username"
                      value={editForm.username}
                      onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                      placeholder="username"
                      autoComplete="off"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-password">Password *</Label>
                    <Input
                      id="edit-password"
                      data-testid="edit-playlist-password"
                      type="password"
                      value={editForm.password}
                      onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                      placeholder="password"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Live preview of all three regenerated URLs. */}
                {editForm.host && editForm.username && editForm.password && (
                  <div className="rounded-md border border-orange-200 bg-orange-50/60 px-3 py-2 text-[11px] font-mono text-orange-900 space-y-1 break-all">
                    <div>
                      <span className="font-sans font-semibold text-orange-700">M3U:</span>{" "}
                      {buildXtreamUrls(editForm).m3u_url}
                    </div>
                    <div>
                      <span className="font-sans font-semibold text-orange-700">EPG:</span>{" "}
                      {buildXtreamUrls(editForm).epg_url}
                    </div>
                    <div>
                      <span className="font-sans font-semibold text-orange-700">B2C:</span>{" "}
                      {buildB2cUrl(editForm).m3u_url.replace(
                        /(b2c:\/\/)[^:]+:[^@]+@/,
                        "$1***:***@"
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            <div>
              <Label htmlFor="edit-pl-desc">Description (optional)</Label>
              <Textarea
                id="edit-pl-desc"
                data-testid="edit-pl-desc"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ou
tline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              data-testid="edit-playlist-save"
              onClick={handleEditSave}
              disabled={editSubmitting}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {editSubmitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cover cache inspector — opens when admin clicks the cover-
          stats icon on a playlist row. */}
      <CoverInspectorDialog
        open={!!inspecting}
        onOpenChange={(o) => !o && setInspecting(null)}
        playlist={inspecting}
      />

      {/* v1.4.38 — Unlock prompt (reseller with playlist lock). Fires
          when the user clicks the pencil icon on a locked row. */}
      <Dialog
        open={!!pendingUnlock}
        onOpenChange={(o) => {
          if (!o) {
            setPendingUnlock(null);
            setUnlockPw("");
            setUnlockErr("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-orange-500" />
              Unlock playlist
            </DialogTitle>
            <DialogDescription>
              Enter your playlist password to view / edit
              {pendingUnlock?.playlist?.name ? ` "${pendingUnlock.playlist.name}"` : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Playlist password</Label>
            <Input
              data-testid="playlist-unlock-input"
              type="password"
              value={unlockPw}
              onChange={(e) => setUnlockPw(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleUnlockSubmit(); }}
              autoFocus
            />
            {unlockErr && (
              <div className="text-xs text-red-600" data-testid="playlist-unlock-error">{unlockErr}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingUnlock(null)}>
              Cancel
            </Button>
            <Button
              data-testid="playlist-unlock-submit"
              onClick={handleUnlockSubmit}
              disabled={unlockBusy}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {unlockBusy ? "Verifying..." : "Unlock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -----------------------------------------------------------------
 * PrewarmCell — per-playlist status pill + Run / Delete controls.
 * Reads the server status payload shape returned by
 * GET /api/admin/playlists/{id}/xtream-prewarm/status:
 *   { status: {status, step, total, label, error}, meta: {...},
 *     has_bundle: bool }
 * ----------------------------------------------------------------- */
function PrewarmCell({ playlist, info, busy, onRun, onDelete, onRefresh }) {
  const isXtream = (playlist.m3u_url || "").includes("/get.php");
  if (!isXtream) {
    return (
      <span
        data-testid={`prewarm-na-${playlist.id}`}
        className="text-xs italic text-slate-400"
      >
        N/A — Xtream-only
      </span>
    );
  }

  const status = info?.status?.status || "idle";
  const meta = info?.meta;
  const hasBundle = !!info?.has_bundle;
  const stepLabel = info?.status?.label;
  const step = info?.status?.step ?? 0;
  const total = info?.status?.total ?? 7;
  const errMsg = info?.status?.error;

  let pill = null;
  if (status === "running") {
    pill = (
      <span
        data-testid={`prewarm-pill-running-${playlist.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[11px] font-medium"
      >
        <RefreshCw className="w-3 h-3 animate-spin" />
        Harvesting {step}/{total}
        {stepLabel ? <span className="text-amber-700">· {stepLabel}</span> : null}
      </span>
    );
  } else if (status === "error") {
    pill = (
      <span
        data-testid={`prewarm-pill-error-${playlist.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-800 border border-rose-300 px-2 py-0.5 text-[11px] font-medium"
        title={errMsg || ""}
      >
        ⚠️ Error
      </span>
    );
  } else if (hasBundle) {
    pill = (
      <span
        data-testid={`prewarm-pill-fresh-${playlist.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-0.5 text-[11px] font-medium"
      >
        ✅ Fresh
      </span>
    );
  } else {
    pill = (
      <span
        data-testid={`prewarm-pill-missing-${playlist.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 border border-slate-300 px-2 py-0.5 text-[11px] font-medium"
      >
        ❌ Never harvested
      </span>
    );
  }

  const fmtAgo = (iso) => {
    if (!iso) return null;
    try {
      const t = new Date(iso).getTime();
      const diff = Date.now() - t;
      if (diff < 60_000) return "just now";
      if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
      if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
      return `${Math.round(diff / 86_400_000)}d ago`;
    } catch {
      return null;
    }
  };
  const fmtBytes = (n) => {
    if (typeof n !== "number") return null;
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        {pill}
        <Button
          size="sm"
          variant="outline"
          className="h-6 px-2 text-[11px]"
          onClick={onRun}
          disabled={busy || status === "running"}
          data-testid={`btn-prewarm-run-${playlist.id}`}
          title="Re-harvest the entire Xtream catalog into a fresh bundle"
        >
          <Zap className="w-3 h-3 mr-1" />
          Run now
        </Button>
        {hasBundle && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={onDelete}
            disabled={busy}
            data-testid={`btn-prewarm-delete-${playlist.id}`}
            title="Delete the on-disk bundle. Devices will fall back to lazy fetches."
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1 text-[11px] text-slate-500 hover:text-slate-700"
          onClick={onRefresh}
          disabled={busy}
          data-testid={`btn-prewarm-refresh-${playlist.id}`}
          title="Refresh status"
        >
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>
      {meta && hasBundle && (
        <div
          className="text-[10px] text-slate-500 font-mono leading-tight"
          data-testid={`prewarm-meta-${playlist.id}`}
        >
          {fmtBytes(meta.bytes)} · {fmtAgo(meta.created_at)} ·{" "}
          live {meta.live_streams?.toLocaleString?.() ?? meta.live_streams} ·{" "}
          vod {meta.vod_streams?.toLocaleString?.() ?? meta.vod_streams} ·{" "}
          series {meta.series?.toLocaleString?.() ?? meta.series}
        </div>
      )}
      {errMsg && status === "error" && (
        <div
          className="text-[10px] text-rose-600 leading-tight truncate max-w-[260px]"
          data-testid={`prewarm-error-${playlist.id}`}
          title={errMsg}
        >
          {errMsg}
        </div>
      )}
    </div>
  );
}
